import type { DependencyId, DependencyType, Project, Task, TaskId } from '@bigint-project/core';

export const MAX_NETWORK_DIAGRAM_NODES = 1_000;
export const MAX_NETWORK_DIAGRAM_EDGES = 5_000;

export interface NetworkNode {
  readonly task: Task;
  readonly layer: number;
  readonly row: number;
}

export interface NetworkEdge {
  readonly id: DependencyId;
  readonly predecessorId: TaskId;
  readonly successorId: TaskId;
  readonly type: DependencyType;
}

export interface NetworkDiagramProjection {
  readonly nodes: readonly NetworkNode[];
  readonly edges: readonly NetworkEdge[];
  readonly error: string | null;
}

/** Builds a stable, iterative layered DAG projection without invoking scheduling. */
export function buildNetworkDiagramProjection(project: Pick<Project, 'tasks' | 'dependencies'>): NetworkDiagramProjection {
  if (project.tasks.length > MAX_NETWORK_DIAGRAM_NODES) {
    return { nodes: [], edges: [], error: `網路圖最多顯示 ${MAX_NETWORK_DIAGRAM_NODES} 項任務；請縮小專案後再試。` };
  }
  if (project.dependencies.length > MAX_NETWORK_DIAGRAM_EDGES) {
    return { nodes: [], edges: [], error: `網路圖最多顯示 ${MAX_NETWORK_DIAGRAM_EDGES} 筆相依；請縮小專案後再試。` };
  }
  const tasksById = new Map(project.tasks.map((task) => [task.id as string, task]));
  const successors = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  const layers = new Map<string, number>();
  for (const task of project.tasks) {
    indegree.set(task.id as string, 0);
    successors.set(task.id as string, []);
    layers.set(task.id as string, 0);
  }
  for (const dependency of project.dependencies) {
    const predecessor = dependency.predecessorId as string;
    const successor = dependency.successorId as string;
    if (!tasksById.has(predecessor) || !tasksById.has(successor) || predecessor === successor) {
      return { nodes: [], edges: [], error: '網路圖包含無效的任務相依參照。' };
    }
    successors.get(predecessor)!.push(successor);
    indegree.set(successor, (indegree.get(successor) ?? 0) + 1);
  }
  const queue = project.tasks.filter((task) => indegree.get(task.id as string) === 0).map((task) => task.id as string);
  const ordered: string[] = [];
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const id = queue[cursor]!;
    ordered.push(id);
    const nextLayer = (layers.get(id) ?? 0) + 1;
    for (const successor of successors.get(id) ?? []) {
      layers.set(successor, Math.max(layers.get(successor) ?? 0, nextLayer));
      const remaining = (indegree.get(successor) ?? 0) - 1;
      indegree.set(successor, remaining);
      if (remaining === 0) queue.push(successor);
    }
  }
  if (ordered.length !== project.tasks.length) {
    return { nodes: [], edges: [], error: '網路圖無法顯示含循環相依的專案。' };
  }
  const rowsByLayer = new Map<number, number>();
  const nodes = ordered.map((id) => {
    const layer = layers.get(id) ?? 0;
    const row = rowsByLayer.get(layer) ?? 0;
    rowsByLayer.set(layer, row + 1);
    return { task: tasksById.get(id)!, layer, row };
  });
  return {
    nodes,
    edges: project.dependencies.map((dependency) => ({
      id: dependency.id,
      predecessorId: dependency.predecessorId,
      successorId: dependency.successorId,
      type: dependency.type,
    })),
    error: null,
  };
}
